import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import { Toaster } from './components/ui/toaster';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import ClientsPage from './pages/ClientsPage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectManagementPage from './pages/ProjectManagementPage';
import ContractsPage from './pages/ContractsPage';
import SalesPage from './pages/SalesPage';
import CommissionsPage from './pages/CommissionsPage';
import UpsellsPage from './pages/UpsellsPage';
import UsersPage from './pages/UsersPage';
import SettingsPage from './pages/SettingsPage';
import ToolkitPage from './pages/ToolkitPage';
import PlannerListPage from './pages/PlannerListPage';
import PlannerEditorPage from './pages/PlannerEditorPage';
import RecruitmentPlannerEditorPage from './pages/RecruitmentPlannerEditorPage';
import FrontEndPipelinePage from './pages/FrontEndPipelinePage';
import CustomerFeedbackPage from './pages/CustomerFeedbackPage';
import CompensationsPage from './pages/CompensationsPage';
import FiscalCreditsPage from './pages/FiscalCreditsPage';
import SalesRosterPage from './pages/SalesRosterPage';
import AdminDebugPMPage from './pages/AdminDebugPMPage';
import './App.css';

// Protected Route component
function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading, hasRole } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !hasRole(allowedRoles)) {
    return <Navigate to="/" replace />;
  }

  return <Layout>{children}</Layout>;
}

// Public Route (redirect if logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients"
        element={
          <ProtectedRoute allowedRoles={['admin', 'hr', 'project_manager', 'supervisor', 'account']}>
            <ClientsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects"
        element={
          <ProtectedRoute>
            <ProjectsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/project-management"
        element={
          <ProtectedRoute allowedRoles={['admin', 'hr', 'project_manager', 'supervisor', 'account', 'venditore']}>
            <ProjectManagementPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contracts"
        element={
          <ProtectedRoute allowedRoles={['admin', 'hr', 'project_manager', 'supervisor', 'account']}>
            <ContractsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/upsells"
        element={
          <ProtectedRoute allowedRoles={['admin', 'hr', 'project_manager', 'supervisor']}>
            <UpsellsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales"
        element={
          <ProtectedRoute allowedRoles={['admin', 'hr', 'project_manager', 'supervisor', 'venditore', 'cliente', 'account']}>
            <SalesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/commissions"
        element={
          <ProtectedRoute allowedRoles={['admin', 'hr', 'project_manager', 'supervisor', 'venditore', 'cliente', 'account']}>
            <CommissionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customer-feedback"
        element={
          <ProtectedRoute allowedRoles={['admin', 'hr', 'account', 'project_manager', 'supervisor']}>
            <CustomerFeedbackPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/compensations"
        element={
          <ProtectedRoute allowedRoles={['admin', 'hr', 'project_manager', 'supervisor', 'venditore']}>
            <CompensationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/toolkit"
        element={
          <ProtectedRoute allowedRoles={['admin', 'hr', 'project_manager', 'supervisor', 'account']}>
            <ToolkitPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/toolkit/planners"
        element={
          <ProtectedRoute allowedRoles={['admin', 'hr', 'project_manager', 'supervisor', 'account']}>
            <PlannerListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/toolkit/planner/new"
        element={
          <ProtectedRoute allowedRoles={['admin', 'hr', 'project_manager', 'supervisor']}>
            <PlannerEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/toolkit/planner/:id"
        element={
          <ProtectedRoute allowedRoles={['admin', 'hr', 'project_manager', 'supervisor', 'account']}>
            <PlannerEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/toolkit/recruitment-planner/new"
        element={
          <ProtectedRoute allowedRoles={['admin', 'hr', 'project_manager', 'supervisor']}>
            <RecruitmentPlannerEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/toolkit/recruitment-planner/:id"
        element={
          <ProtectedRoute allowedRoles={['admin', 'hr', 'project_manager', 'supervisor', 'account']}>
            <RecruitmentPlannerEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/frontend-pipeline"
        element={
          <ProtectedRoute allowedRoles={['admin', 'hr', 'account', 'project_manager', 'supervisor']}>
            <FrontEndPipelinePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute allowedRoles={['admin', 'hr']}>
            <UsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute allowedRoles={['admin', 'hr']}>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/fiscal-credits"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <FiscalCreditsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales-roster"
        element={
          <ProtectedRoute allowedRoles={['admin', 'hr']}>
            <SalesRosterPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/debug-pm"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminDebugPMPage />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
